
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { namespace, Getter, Mutation, Action } from "vuex-class";
import TextHighlight from "vue-text-highlight";
import vSelect from "vue-select";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import SureViewIcon from "@/components/SureViewIcon.vue";

import GenericUpdateModal, { ModalItem } from "./generic-update-modal.vue";
import { UserPermissions } from "@/store/types";

export interface TableHeader {
	title: string;
	key: string;
	order: number;
	sortOrder: number;
	sortOrderReversed: boolean;
	description?: string;
	searchable?: boolean;
	visible?: boolean;
	dataType?: string;
	useCustomCell?: boolean;
	width?: string;
}

@Component({
	components: {
		"v-select": vSelect,
		"text-highlight": TextHighlight,
		"generic-update-modal": GenericUpdateModal,
		scrollbar: VuePerfectScrollbar,
		"sureview-icon": SureViewIcon,
	}
})
export default class GenericTable extends Vue {
	$refs!: {
		genericTable: any;
		GenericUpdateModal: GenericUpdateModal;
	};

	@Prop() public columns!: TableHeader[];
	@Prop() public modalItems!: ModalItem[];
	@Prop() public editPermissions!: any[];
	@Prop() public deletePermissions!: any[];
	@Prop() public addPermissions!: any[];
	@Prop({ default: "lg" }) modalSize!: string;

	// When unset(undefined) - generic-table will proceed with existing checks
	// for add/edit controls.
	// When set, visibility of those controls will solely depends on readonly prop value
	@Prop() public readonly: boolean | undefined;

	@Prop({ type: String }) public canEditProperty;
	@Prop({ default: [] }) public dataList: any[];
	@Prop() public term!: string;
	@Prop() public modalTitle!: string;
	@Prop() public rightSubTitle: string = "";
	@Prop({ type: Boolean, default: true }) useModal: boolean;
	@Prop({ type: Boolean, default: false }) isLoadingData: boolean;

	@Prop() public onSave;
	@Prop() public onDelete;
	@Prop() public onAddNew;
	@Prop() public onModalOpen;

	@Getter getPermissions: any;

	private renderData: any[] = null;
	private showFilter: boolean = false;
	private tableSearch: string = "";

	private mounted() {
		this.updateRenderData(this.dataList);
	}

	private defaultRenderCount: number = 50;
	private renderCount: number = this.defaultRenderCount;

	private isRenderUpdating: boolean = false;

	// Filter the data by the columns that are requested
	private filterByColumn(row: any[]) {
		let renderList = [];
		for (let key in row) {
			let filteredColumns = this.columns.filter(function(el) {
				return el.key == key && el.visible;
			});
			if (filteredColumns.length > 0) {
				renderList.push({
					value: row[key],
					key: key,
					type: filteredColumns[0].dataType,
					useCustomCell: filteredColumns[0].useCustomCell,
					sortOrder: filteredColumns[0].order,
					width: filteredColumns[0].width
				});
			}
		}
		return renderList.sort((a, b) => a.sortOrder - b.sortOrder);
	}

	private checkPermissions(row, requiredPermission, current, readOnly) {
		if (this.readonly !== undefined) {
			return !this.readonly;
		}

		if (readOnly) {
			return false;
		}

		if (this.canEditProperty) {
			return row[this.canEditProperty];
		}

		// if required permission is blank, we dont need a specific permission
		if (requiredPermission == null) {
			return true;
		}

		if (requiredPermission && current) {
			// look through the users permission to see if we have one that matches
			let userHasPermission = requiredPermission.some(r => {
				if (typeof current[r] == "boolean") {
					// check we have the permission set, and that it is set to true
					return Object.keys(current).includes(r) && current[r] == true;
				}

				// check we have the permission set
				return Object.keys(current).includes(r);
			});

			return userHasPermission;
		}

		return false;
	}

	// Return the list of columns that are tagged as visible
	private visibleColumns() {
		return this.columns
			.filter(function(el) {
				return el.visible;
			})
			.sort(function(a, b) {
				return a.order - b.order;
			});
	}

	private get numberOfVisibleColumns(): number {
		if(this.columns && this.columns.length > 0) {
			return this.visibleColumns().length;
		}

		return 0;
	}

	@Watch("dataList", { deep: true })
	private updateRenderData(value: any) {
		this.searchTable(this.tableSearch);
	}

	private async openModel(row, readOnly?, addNew?) {
		if (this.onModalOpen) {
			let returnValue = await this.onModalOpen(row);
			if (returnValue != null) {
				row = returnValue;
			}
		}
		this.$refs.GenericUpdateModal.showUpdateDialog(row, readOnly, addNew);
	}

	private formatList(data) {
		let output = "";
		for (let value in data) {
			if (output != "") {
				output += ", ";
			}
			output += data[value];
		}
		return output;
	}

	private get getRenderData() {
		if (this.renderData == null) {
			return this.renderData;
		}
		let startCount = 0;
		return this.renderData.slice(startCount, this.renderCount);
	}

	// Filter the searchable columns by search string
	private async searchTable(searchString) {
		this.renderCount = this.defaultRenderCount;
		if (searchString === "") {
			// Nothing to search. Show the whole results
			this.renderData = this.dataList.filter(function(el) {
				if (el.hidden == true) {
					return false;
				}
				return true;
			});
		} else {
			// Get all the columns that are searchable
			let searchableColumns = this.columns.filter(function(columnEl) {
				return columnEl.searchable == true && columnEl.visible;
			});
			// Search the data that is searchable
			this.renderData = this.dataList.filter(function(dataEl) {
				for (let index in searchableColumns) {
					let text = typeof searchString === "string" ? searchString : searchString.target.value; // keyboard event

					if (
						searchString &&
						dataEl[searchableColumns[index].key] &&
						dataEl[searchableColumns[index].key]
							.toString()
							.toLowerCase()
							.includes(text.toLowerCase())
					) {
						return true;
					}
				}
				return false;
			});
		}
	}

	private onScrollBottom() {
		if (!this.isRenderUpdating && this.renderData != null && this.renderCount < this.renderData.length) {
			this.isRenderUpdating = true;
			setTimeout(
				function() {
					this.renderCount += 25;
					if (this.renderCount > this.renderData.length) {
						this.renderCount = this.renderData.length;
					}
					this.isRenderUpdating = false;
				}.bind(this),
				500
			);
		}
	}

	private get isAddVisible() {
		if (this.readonly !== undefined) {
			return !this.readonly;
		}
		return this.getPermissions.canEditSiteSetup && this.useModal;
	}
}
